import React, { useEffect, useMemo, useState } from "react";
import { difference, map, uniq, without } from "lodash";
import styled from "styled-components";
import { border } from "@glossy";
import { Interval, PeriodRange, PeriodType } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Timezone } from "@linkfluence/models/timezone";
import Icon from "@ui-kit/icon";
import Menu from "@ui-kit/popover/menu";
import Tooltip from "@ui-kit/tooltip";
import FilterBar from "@radarly/components/filterbar";
import Button from "@radarly/components/filterbar/gigantic-button";
import PeriodSelector from "@radarly/components/filterbar/period-selector";
import useNoCopyModal from "@radarly/components/insight-pages/editor/row/card/toolbar/useNoCopyModal";
import usePasteModal from "@radarly/components/insight-pages/editor/row/card/toolbar/usePasteModal";
import {
  useContextualWorkspaceMilestones,
  useVisibleBanner,
} from "@radarly/core/context/use-cases";
import { useFeature } from "@radarly/core/features/use-cases";
import useCopyPasteEditor from "@radarly/core/insight-pages/useCopyPasteEditor";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { useCurrentWorkspaceId } from "@radarly/core/workspace/use-cases";
import { tr } from "@radarly/i18n";
import { Card } from "@radarly/models/cards";
import { Clause } from "@radarly/models/clause";
import {
  extractAndSplitClausesByFields,
  extractFieldsFromClause,
} from "@radarly/models/clause/extractor";
import {
  getFieldFromInnerClause,
  getUsedFieldsFromClause,
} from "@radarly/models/clause/getter";
import { mergeClauses } from "@radarly/models/clause/merge";
import { getProjectLimits } from "@radarly/models/project";
import Chip from "./chip";
import NewFilter from "./new-filter";
import Permanent from "./permanent";

export interface IProps {
  period: PeriodRange;
  setPeriod: (period: PeriodRange) => void;
  fields: Facet[];
  clause: Clause;
  setClause: (clause: Clause) => void;
  applyClause: (clause: Clause) => void;
  onApply: () => void;
  onClose: () => void;
  onPasteFilters?: (clause: Clause) => void;
  hasClause?: boolean;
  hideFreeFilters: boolean;
  timezone: Timezone;
  sticky?: boolean;
  permanentLabels?: Partial<Record<Facet, string>>;
  isPeriodSelectorEnabled?: boolean;
  allowCopyPaste?: boolean;
  card?: Card;
  isDisabled?: boolean;
  disabledTooltipLabel?: string;
  allowBeyondLimit?: boolean;
  excludedFacets?: Facet[];
  customCategories?: Facet[];
  hiddenPeriods?: PeriodType[];
  isMaxLimitToday?: boolean;
  limitTo100Days?: boolean;
  isVisibleAndDisabled?: boolean;
}

const DEFAULT_CLAUSE = { and: [] };

type Action = "copy" | "paste" | "reset";

const MainFilters = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const Temporary = styled.div`
  height: 100%;
  margin-left: 6px;
  display: flex;
  align-items: center;
`;

const StyledResetButton = styled(Button)`
  border-left: 1px solid ${border};
`;

const StyledMenu = styled(Menu)`
  height: 100%;
`;

const CustomLabel = styled.div`
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 140px;
`;

function getFieldsFromClauseAndPermanentFilters(
  clause: Clause,
  fields: string[]
) {
  const allFields = [...fields, ...extractFieldsFromClause(clause)];
  return uniq(
    allFields.map((field) => {
      if (field === "inRelationship" || field === "hasChildren") {
        return Facet.MaritalStatus;
      }
      return field;
    })
  );
}

const RadarlyFilterbar = ({
  period,
  setPeriod,
  fields,
  clause,
  setClause,
  onApply,
  onClose,
  applyClause,
  onPasteFilters = () => null,
  hasClause,
  hideFreeFilters,
  sticky,
  permanentLabels,
  timezone,
  isPeriodSelectorEnabled = false,
  allowCopyPaste = false,
  card,
  isDisabled,
  disabledTooltipLabel,
  allowBeyondLimit,
  excludedFacets,
  customCategories,
  hiddenPeriods,
  isMaxLimitToday,
  limitTo100Days,
  isVisibleAndDisabled,
}: IProps) => {
  const [currentFields, setCurrentFields] = useState(
    getFieldsFromClauseAndPermanentFilters(clause, fields)
  );
  const project = useCurrentProject();
  const workspaceId = useCurrentWorkspaceId();
  const enabledMilestones = useFeature("period_selector_milestones");
  const milestones = useContextualWorkspaceMilestones();
  const locale = useCurrentUserLocale();
  const banner = useVisibleBanner();
  const { PasteModal, onOpenPasteModal } = usePasteModal(
    (_position, _row, clause) => onPasteFilters(clause),
    () => null,
    () => null,
    locale,
    "modal_paste_card_editor"
  );
  const { NoCopyModal, onOpenNoCopyModal } = useNoCopyModal(
    () => null,
    locale,
    "modal_nocopy_card_editor"
  );
  const { copyFilters, isCardFiltered } = useCopyPasteEditor(
    locale,
    project.id,
    workspaceId
  );

  const { permanent } = extractAndSplitClausesByFields(clause, currentFields);
  const addNewClause = (newClause: Clause) => {
    const field = getFieldFromInnerClause(newClause.and[0]);
    setCurrentFields([...currentFields, field]);
    applyClause(mergeClauses(clause, newClause));
  };
  const restFields = difference(currentFields, fields);
  const usedFields = getUsedFieldsFromClause(clause);
  const projectLimits = useMemo(
    () => getProjectLimits(project, Interval.Day, isMaxLimitToday),
    [isMaxLimitToday, project]
  );

  const onRemoveClause = (field: string) => {
    setCurrentFields(without(currentFields, field));
    applyClause(
      permanent[field] ? permanent[field].rest : clause || DEFAULT_CLAUSE
    );
  };
  const onClearClause = () => {
    setCurrentFields(fields);
    applyClause(null);
  };

  useEffect(() => {
    setCurrentFields(getFieldsFromClauseAndPermanentFilters(clause, fields));
  }, [clause, fields]);

  const onClickMenu = async (action: Action) => {
    switch (action) {
      case "copy":
        if (isCardFiltered(card)) {
          copyFilters(card);
          break;
        }
        onOpenNoCopyModal();
        break;
      case "paste":
        onOpenPasteModal(null, null, "paste_filters");
        break;
      case "reset":
        onClearClause();
        break;
    }
  };

  return (
    <FilterBar
      sticky={sticky}
      isBelowBanner={!!banner}
      disabled={isDisabled}
      isVisibleAndDisabled={isVisibleAndDisabled}
      disabledTooltipLabel={disabledTooltipLabel}
    >
      <PasteModal />
      <NoCopyModal />
      <MainFilters>
        {!!period && isPeriodSelectorEnabled && (
          <PeriodSelector
            limits={projectLimits}
            milestones={!enabledMilestones ? [] : milestones}
            locale={locale}
            period={period}
            timezone={timezone}
            customHour
            customPeriod
            onChange={setPeriod}
            allowBeyondLimit={allowBeyondLimit}
            hiddenPeriods={hiddenPeriods}
            limitTo100Days={limitTo100Days}
          />
        )}
        {map(fields, (field) => (
          <Permanent
            key={field}
            field={field}
            clause={permanent[field] ? permanent[field].clause : DEFAULT_CLAUSE}
            restClause={
              permanent[field]
                ? permanent[field].rest
                : clause || DEFAULT_CLAUSE
            }
            setClause={setClause}
            onApply={onApply}
            onClose={onClose}
            label={permanentLabels ? permanentLabels[field] : null}
            period={period}
          />
        ))}
        {!hideFreeFilters && (
          <>
            <Temporary>
              {map(restFields, (field) => (
                <Chip
                  key={field}
                  field={field}
                  clause={
                    permanent[field] ? permanent[field].clause : DEFAULT_CLAUSE
                  }
                  restClause={
                    permanent[field]
                      ? permanent[field].rest
                      : clause || DEFAULT_CLAUSE
                  }
                  setClause={setClause}
                  onApply={onApply}
                  onClose={onClose}
                  onRemove={() => onRemoveClause(field)}
                  period={period}
                />
              ))}
            </Temporary>
            <NewFilter
              setClause={addNewClause}
              locale={locale}
              usedFields={usedFields}
              period={period}
              excludedFacets={excludedFacets}
              customCategories={customCategories}
            />
          </>
        )}
      </MainFilters>

      {hasClause && !allowCopyPaste && (
        <Tooltip
          tooltip={tr(locale, "FILTER_PANEL::REMOVE_ALL_FILTERS")}
          placement="top"
        >
          <StyledResetButton
            glyph="round-arrow-cross"
            onClick={onClearClause}
          />
        </Tooltip>
      )}

      {allowCopyPaste && !!card && (
        <StyledMenu
          values={[
            {
              id: "copy",
              label: tr(locale, "FILTER_PANEL::COPY_FILTERS"),
            },
            {
              id: "paste",
              label: (
                <CustomLabel>
                  {tr(locale, "FILTER_PANEL::PASTE_FILTERS")}{" "}
                  <Icon glyph="chevron-right" size={12} />
                </CustomLabel>
              ),
            },
            {
              id: "reset",
              label: tr(locale, "FILTER_PANEL::RESET_FILTERS"),
            },
          ]}
          onSelect={onClickMenu}
          disclosure={<Button glyph="dots-3-h" />}
        />
      )}
    </FilterBar>
  );
};

export default RadarlyFilterbar;
